import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';

import { api } from '@/lib/api';
import { useHasPermission } from '@/lib/permissions';
import { VEHICLE_STATUS_LABELS, VEHICLE_STATUS_VARIANT, fuelLabel } from '@/lib/vehicles';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { QueryError } from '@/components/QueryError';
import { VehicleMissionsTab } from '@/components/vehicles/VehicleMissionsTab';
import { VehicleMaintenanceTab } from '@/components/vehicles/VehicleMaintenanceTab';
import { VehicleFuelTab } from '@/components/vehicles/VehicleFuelTab';

import type { Vehicle } from '@logistech/ui-shared';

export const Route = createFileRoute('/_authed/fleet/$id')({ component: FleetDetailPage });

function FleetDetailPage() {
  const { id } = Route.useParams();
  // Défense en profondeur : onglet masqué sans la permission de lecture du service qui le sert
  const canReadMissions = useHasPermission('missions.read');
  const canReadMaintenance = useHasPermission('maintenance.read');
  const canReadFuel = useHasPermission('fuel.read');

  const query = useQuery({
    queryKey: ['vehicle', id],
    queryFn: async () => (await api.get<Vehicle>(`/vehicles/${id}`)).data,
  });
  const { data: vehicle, isLoading } = query;

  if (isLoading) return <div className="p-6"><Skeleton className="h-48 w-full" /></div>;
  if (query.isError) return <div className="p-6"><QueryError query={query} /></div>;
  if (!vehicle) return <div className="p-6 text-muted-foreground">Véhicule introuvable</div>;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/fleet"><ArrowLeft className="h-4 w-4 mr-1" /> Retour</Link>
        </Button>
        <h1 className="text-2xl font-bold">{vehicle.plate}</h1>
        <Badge variant={VEHICLE_STATUS_VARIANT[vehicle.status]}>{VEHICLE_STATUS_LABELS[vehicle.status] ?? vehicle.status}</Badge>
      </div>

      <Tabs defaultValue="info">
        <TabsList className="max-w-full overflow-x-auto justify-start">
          <TabsTrigger value="info">Informations</TabsTrigger>
          {canReadMissions && <TabsTrigger value="missions">Missions</TabsTrigger>}
          {canReadMaintenance && <TabsTrigger value="maintenance">Maintenance</TabsTrigger>}
          {canReadFuel && <TabsTrigger value="fuel">Carburant</TabsTrigger>}
        </TabsList>

        <TabsContent value="info">
          <Card>
            <CardHeader><CardTitle>Fiche technique</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 gap-4 text-sm">
              <Field label="Immatriculation" value={vehicle.plate} />
              <Field label="Marque" value={vehicle.make ?? '—'} />
              <Field label="Modèle" value={vehicle.model ?? '—'} />
              <Field label="Année" value={vehicle.year?.toString() ?? '—'} />
              <Field label="Carburant" value={fuelLabel(vehicle.fuelType)} />
              <Field label="Capacité" value={vehicle.capacityTons != null ? `${vehicle.capacityTons} t` : '—'} />
              <Field label="Odomètre" value={`${vehicle.odometerKm.toLocaleString('fr-FR')} km`} />
              <Field label="Heures moteur" value={`${vehicle.engineHours.toFixed(0)} h`} />
              <Field label="IMEI tracker" value={vehicle.deviceImei ?? '—'} />
            </CardContent>
          </Card>
        </TabsContent>

        {canReadMissions && (
          <TabsContent value="missions"><VehicleMissionsTab vehicleId={vehicle.id} /></TabsContent>
        )}

        {canReadMaintenance && (
          <TabsContent value="maintenance"><VehicleMaintenanceTab vehicleId={vehicle.id} /></TabsContent>
        )}

        {canReadFuel && (
          <TabsContent value="fuel"><VehicleFuelTab vehicleId={vehicle.id} /></TabsContent>
        )}
      </Tabs>
    </div>
  );
}

const Field = ({ label, value }: { label: string; value: string }) => (
  <div>
    <dt className="text-muted-foreground text-xs uppercase tracking-wide">{label}</dt>
    <dd className="font-medium mt-0.5">{value}</dd>
  </div>
);
